import React, { useState, useEffect, useRef } from 'react'
import { useTeamMembers, TeamMember } from '../hooks/useTeamMembers'
import { useAuth } from '../contexts/AuthContext'
import { useUserPermissions } from '../contexts/UserPermissionsContext'
import { useEmployeeDirectory } from '../contexts/EmployeeDirectoryContext'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { HiUserGroup } from 'react-icons/hi2'
import { apiClient } from '../common/apiClient'
import { getApiUrl } from '../config/constants'

interface PinnedChat {
  id: number
  chat_id: string
  chat_topic: string | null
  chat_type: string
  web_url: string | null
  sort_order: number
  member_email: string | null
  profilePicture?: string
}

const DRAWER_STATE_KEY = 'teambar_drawer_expanded'

const FloatingTeamBar: React.FC = () => {
  const { user, getGraphAccessToken } = useAuth()
  const { userPermissions } = useUserPermissions()
  const { getDirectory } = useEmployeeDirectory()
  const effectiveEmail = userPermissions.isOverride ? userPermissions.overrideEmail : user?.email
  const [isVisible, setIsVisible] = useState(false)
  const [showTeamBar, setShowTeamBar] = useState(true)
  const [layout, setLayout] = useState<'horizontal' | 'vertical'>('horizontal')
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem(DRAWER_STATE_KEY)
    return saved !== null ? saved === 'true' : false // Default to minimized
  })
  const verticalContentRef = useRef<HTMLDivElement>(null)
  const [managers, setManagers] = useState<TeamMember[]>([])
  const [pinnedChats, setPinnedChats] = useState<PinnedChat[]>([])

  const toggleDrawer = () => {
    const newState = !isExpanded
    setIsExpanded(newState)
    localStorage.setItem(DRAWER_STATE_KEY, String(newState))
  }

  useEffect(() => {
    const fetchPreference = async () => {
      if (!user?.email) return
      try {
        const data = await apiClient.get<{ team_bar_layout: 'horizontal' | 'vertical'; show_team_bar: boolean }>(
          `/api/user-preferences?email=${encodeURIComponent(user.email)}`
        )
        setLayout(data.team_bar_layout ?? 'horizontal')
        setShowTeamBar(data.show_team_bar ?? true)
      } catch (error) {
        console.error('Error fetching team bar preference:', error)
      }
    }
    fetchPreference()

    const handlePreferenceChange = (e: CustomEvent<{ team_bar_layout?: 'horizontal' | 'vertical'; show_team_bar?: boolean }>) => {
      const newLayout = e.detail?.team_bar_layout
      if (newLayout === 'horizontal' || newLayout === 'vertical') {
        setLayout(newLayout)
      }
      if (typeof e.detail?.show_team_bar === 'boolean') {
        setShowTeamBar(e.detail.show_team_bar)
      }
    }
    window.addEventListener('user-preferences-changed', handlePreferenceChange as EventListener)
    return () => window.removeEventListener('user-preferences-changed', handlePreferenceChange as EventListener)
  }, [user?.email])

  // Fetch pinned chats and their profile pictures
  useEffect(() => {
    const fetchPinnedChats = async () => {
      if (!user?.email) return
      try {
        const data = await apiClient.get<PinnedChat[]>(
          `/api/user-preferences/pinned-chats?email=${encodeURIComponent(user.email)}`
        )
        if (!data?.length) {
          setPinnedChats([])
          return
        }

        // Fetch profile pictures for 1:1 chats
        const token = await getGraphAccessToken()
        const chatsWithPhotos = await Promise.all(
          data.map(async (chat) => {
            if (chat.chat_type === 'oneOnOne' && chat.member_email && token) {
              try {
                const photoResponse = await fetch(`${getApiUrl()}/api/auth/graph-photo`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: chat.member_email, accessToken: token, size: '96x96' }),
                })
                if (photoResponse.ok) {
                  const result = await photoResponse.json()
                  if (result.success && result.profilePicture) {
                    return { ...chat, profilePicture: result.profilePicture }
                  }
                }
              } catch { /* ignore */ }
            }
            return chat
          })
        )
        setPinnedChats(chatsWithPhotos)
      } catch (error) {
        console.error('Error fetching pinned chats:', error)
      }
    }
    fetchPinnedChats()

    // Listen for pinned chats updates
    const handlePinnedChatsChange = () => fetchPinnedChats()
    window.addEventListener('pinned-chats-changed', handlePinnedChatsChange)
    return () => window.removeEventListener('pinned-chats-changed', handlePinnedChatsChange)
  }, [user?.email, getGraphAccessToken])

  const { teamMembers, isLoading } = useTeamMembers({
    userTeam: userPermissions.Team__c,
    userCompany: userPermissions.Company__c,
    loggedInUserEmail: user?.email,
    effectiveUserEmail: effectiveEmail,
  })

  // Fetch manager chain (managers you report to)
  useEffect(() => {
    const fetchManagers = async () => {
      // Wait for userPermissions to be fully loaded (has Id means it's loaded from Salesforce)
      if (!userPermissions.Id || !effectiveEmail) return

      // User might not have a manager (e.g., Tuan)
      if (!userPermissions.Manager__c) {
        setManagers([])
        return
      }

      try {
        // Fetch the fixed employee directory to build the manager chain.
        const allEmployees = await getDirectory()
        if (!allEmployees?.length) return

        // Build manager chain up the hierarchy
        const managerChain: any[] = []
        let currentManagerId = userPermissions.Manager__c
        const visited = new Set<string>()

        while (currentManagerId && !visited.has(currentManagerId)) {
          visited.add(currentManagerId)
          const manager = allEmployees.find((e: any) => e.Id === currentManagerId)
          if (!manager) break
          managerChain.push(manager)
          currentManagerId = manager.Manager__c
        }

        if (managerChain.length === 0) {
          setManagers([])
          return
        }

        // Reverse so it goes from top (Tuan) to bottom (direct manager)
        let finalChain = [...managerChain].reverse()

        // Hardcoded organizational structure fixes (same as TeamMemberModal)
        const tuanEmail = 'tuan@duotax.com.au'
        const andyEmail = 'andy@duotax.com.au'
        const quocEmail = 'quoc@duoqs.com.au'

        const tuanIndex = finalChain.findIndex(m => m.Email?.toLowerCase() === tuanEmail)
        const quocIndex = finalChain.findIndex(m => m.Email?.toLowerCase() === quocEmail)

        // If Quoc is at the top but Tuan isn't there, inject Tuan
        if (quocIndex === 0 && tuanIndex === -1) {
          const tuan = allEmployees.find((e: any) => e.Email?.toLowerCase() === tuanEmail)
          if (tuan) finalChain.unshift(tuan)
        }

        // Recalculate indices
        const currentTuanIndex = finalChain.findIndex(m => m.Email?.toLowerCase() === tuanEmail)
        const currentAndyIndex = finalChain.findIndex(m => m.Email?.toLowerCase() === andyEmail)

        // If Tuan is present but Andy is missing, inject Andy after Tuan
        if (currentTuanIndex !== -1 && currentAndyIndex === -1) {
          const andy = allEmployees.find((e: any) => e.Email?.toLowerCase() === andyEmail)
          if (andy) finalChain.splice(currentTuanIndex + 1, 0, andy)
        }

        // Final reorder: Tuan first, Andy second, then others
        const finalTuanIdx = finalChain.findIndex(m => m.Email?.toLowerCase() === tuanEmail)
        const finalAndyIdx = finalChain.findIndex(m => m.Email?.toLowerCase() === andyEmail)
        const reordered: any[] = []

        if (finalTuanIdx !== -1) reordered.push(finalChain[finalTuanIdx])
        if (finalAndyIdx !== -1 && finalTuanIdx !== finalAndyIdx) reordered.push(finalChain[finalAndyIdx])

        finalChain.forEach((manager, idx) => {
          if (idx !== finalTuanIdx && idx !== finalAndyIdx) reordered.push(manager)
        })

        // Fetch profile pictures for managers
        const token = await getGraphAccessToken()
        const managersWithPhotos: TeamMember[] = await Promise.all(
          reordered.map(async (manager) => {
            let profilePicture: string | undefined
            if (token && manager.Email) {
              try {
                const photoResponse = await fetch(`${getApiUrl()}/api/auth/graph-photo`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: manager.Email, accessToken: token, size: '96x96' }),
                })
                if (photoResponse.ok) {
                  const result = await photoResponse.json()
                  if (result.success && result.profilePicture) {
                    profilePicture = result.profilePicture
                  }
                }
              } catch { /* ignore */ }
            }
            return {
              name: manager.Name,
              email: manager.Email,
              profilePicture,
              isManager: true,
            }
          })
        )
        setManagers(managersWithPhotos)
      } catch (error) {
        console.error('Error fetching managers:', error)
      }
    }
    fetchManagers()
  }, [userPermissions.Id, userPermissions.Manager__c, effectiveEmail, getDirectory, getGraphAccessToken])

  // Combine team members with managers (managers first, deduplicated)
  // Managers are already in correct order: Tuan → Andy → other managers
  const teamMemberEmails = new Set(teamMembers.map(m => m.email.toLowerCase()))
  const uniqueManagers = managers.filter(m => !teamMemberEmails.has(m.email.toLowerCase()))
  const allMembers = [...uniqueManagers, ...teamMembers]
  const otherMembers = allMembers
    .filter(m => m.email.toLowerCase() !== effectiveEmail?.toLowerCase())
    .sort((a, b) => {
      // Managers always come first (they're already in correct order from uniqueManagers)
      const aIsManager = uniqueManagers.some(mgr => mgr.email.toLowerCase() === a.email.toLowerCase())
      const bIsManager = uniqueManagers.some(mgr => mgr.email.toLowerCase() === b.email.toLowerCase())
      if (aIsManager && !bIsManager) return -1
      if (!aIsManager && bIsManager) return 1
      if (aIsManager && bIsManager) {
        // Both are managers - preserve hierarchy order (from uniqueManagers)
        return uniqueManagers.findIndex(m => m.email.toLowerCase() === a.email.toLowerCase()) -
               uniqueManagers.findIndex(m => m.email.toLowerCase() === b.email.toLowerCase())
      }
      // Both are team members - sort by join date
      if (!a.joinedDate) return 1
      if (!b.joinedDate) return -1
      return new Date(a.joinedDate).getTime() - new Date(b.joinedDate).getTime()
    })

  // Trigger fade-in after team members load successfully
  useEffect(() => {
    if (!isLoading && otherMembers.length > 0) {
      const timer = setTimeout(() => setIsVisible(true), 50)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(false)
    }
  }, [isLoading, otherMembers.length])

  // Dispatch width to Layout when in vertical mode (acts as right sidebar)
  useEffect(() => {
    if (layout === 'vertical' && isExpanded) {
      // Width of avatar (32px) + small padding
      window.dispatchEvent(new CustomEvent('teambar-width-changed', { detail: { width: 48 } }))
    } else {
      window.dispatchEvent(new CustomEvent('teambar-width-changed', { detail: { width: 0 } }))
    }
  }, [layout, isExpanded])

  // Don't render if disabled or no team members
  if (!showTeamBar || otherMembers.length === 0) return null

  const openTeamsChat = (email: string) => {
    window.location.href = `msteams://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(email)}`
  }

  const openTeamsChatById = (chatId: string) => {
    // Use Teams deep link protocol to open directly in Teams app
    window.location.href = `msteams://teams.microsoft.com/l/chat/${chatId}/0`
  }

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const getChatInitials = (topic: string | null, chatType: string) => {
    if (!topic) return chatType === 'oneOnOne' ? '1:1' : 'GC'
    return topic.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  // Vertical layout - right sidebar
  if (layout === 'vertical') {
    return (
      <div className={`fixed top-0 right-0 z-50 hidden lg:flex flex-col items-center h-screen transition-opacity duration-300 ease-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        {/* Teams icon toggle - at top */}
        <div className="group relative mt-2 mr-6">
          <button
            onClick={toggleDrawer}
            className="flex items-center justify-center p-1.5 hover:opacity-70 transition-opacity"
          >
            <img src="/teams_logo.png" alt="Teams" className="h-5 w-5 object-contain" />
          </button>
          <span className="absolute top-0 right-12 w-48 px-2 py-1.5 bg-gray-900 text-white text-xs rounded text-center opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity pointer-events-none">
            Quick access to message your team.
          </span>
        </div>
        {/* Content - vertical list of avatars */}
        <div ref={verticalContentRef} className={`flex flex-col items-center backdrop-blur-sm bg-white/50 rounded-lg transition-all duration-300 ease-out mr-6 ${isExpanded ? 'px-1.5 py-2 opacity-100' : 'px-1.5 py-0 opacity-0 pointer-events-none'}`}>
          {/* Pinned chats first */}
          {pinnedChats.map((chat) => (
            <div
              key={chat.chat_id}
              onClick={() => openTeamsChatById(chat.chat_id)}
              className="group relative cursor-pointer -mb-0.5"
            >
              {chat.profilePicture ? (
                <Avatar className="h-8 w-8 ring-2 ring-purple-200 hover:ring-purple-400 transition-all">
                  <AvatarImage src={chat.profilePicture} alt={chat.chat_topic || 'Chat'} />
                  <AvatarFallback className="text-[10px] bg-purple-100 text-purple-700">
                    {getChatInitials(chat.chat_topic, chat.chat_type)}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div className="w-8 flex flex-col items-center gap-0.5 py-1.5 bg-purple-100 hover:bg-purple-200 rounded-full ring-2 ring-white transition-colors">
                  <HiUserGroup className="w-4 h-4 text-purple-700 flex-shrink-0" />
                  <span className="text-[10px] font-medium text-purple-700 [writing-mode:vertical-rl] [text-orientation:mixed]">
                    {chat.chat_topic || 'Chat'}
                  </span>
                </div>
              )}
              <span className="absolute top-1/2 -translate-y-1/2 right-full mr-2 px-2 py-0.5 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity pointer-events-none">
                {chat.chat_topic || 'Chat'}
              </span>
            </div>
          ))}
          {/* Separator if there are pinned chats */}
          {pinnedChats.length > 0 && otherMembers.length > 0 && (
            <div className="w-6 h-px bg-gray-300 my-1.5" />
          )}
          {/* Team members */}
          {otherMembers.map((member) => (
            <div
              key={member.email}
              onClick={() => openTeamsChat(member.email)}
              className="group relative cursor-pointer -mb-0.5 last:mb-0"
            >
              <Avatar className="h-8 w-8 ring-2 ring-white hover:ring-blue-400 transition-all">
                <AvatarImage src={member.profilePicture} alt={member.name} />
                <AvatarFallback className="text-[10px] bg-gray-100">{getInitials(member.name)}</AvatarFallback>
              </Avatar>
              <span className="absolute top-1/2 -translate-y-1/2 right-full mr-2 px-2 py-0.5 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity pointer-events-none">
                {member.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Horizontal layout - slides sideways (default)
  const totalItems = pinnedChats.length + otherMembers.length
  return (
    <div className={`fixed top-0 right-4 z-50 hidden lg:flex transition-opacity duration-300 ease-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="flex items-center transition-all duration-300 ease-out">
        {/* Content - animates width */}
        <div className={`flex items-center backdrop-blur-sm bg-white/50 rounded-b-lg transition-all duration-300 ease-out ${isExpanded ? 'px-4 py-2 opacity-100' : 'px-0 py-2 opacity-0 pointer-events-none'}`}>
          {/* Pinned chats first */}
          {pinnedChats.map((chat, i) => (
            <div
              key={chat.chat_id}
              onClick={() => openTeamsChatById(chat.chat_id)}
              className="group relative -mr-0.5 cursor-pointer"
              style={{ zIndex: totalItems - i }}
            >
              {chat.profilePicture ? (
                <Avatar className="h-8 w-8 ring-2 ring-purple-200 hover:ring-purple-400 hover:z-50 transition-all">
                  <AvatarImage src={chat.profilePicture} alt={chat.chat_topic || 'Chat'} />
                  <AvatarFallback className="text-[10px] bg-purple-100 text-purple-700">
                    {getChatInitials(chat.chat_topic, chat.chat_type)}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div className="h-8 flex items-center gap-1 px-2 bg-purple-100 hover:bg-purple-200 rounded-full ring-2 ring-white hover:z-50 transition-colors whitespace-nowrap">
                  <HiUserGroup className="w-3.5 h-3.5 text-purple-700 flex-shrink-0" />
                  <span className="text-xs font-medium text-purple-700">
                    {chat.chat_topic || 'Chat'}
                  </span>
                </div>
              )}
              <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity pointer-events-none">
                {chat.chat_topic || 'Chat'}
              </span>
            </div>
          ))}
          {/* Separator if there are pinned chats */}
          {pinnedChats.length > 0 && otherMembers.length > 0 && (
            <div className="h-6 w-px bg-gray-300 mx-2" />
          )}
          {/* Team members */}
          {otherMembers.map((member, i) => (
            <div
              key={member.email}
              onClick={() => openTeamsChat(member.email)}
              className="group relative -mr-0.5 last:mr-0 cursor-pointer"
              style={{ zIndex: otherMembers.length - i }}
            >
              <Avatar className="h-8 w-8 ring-2 ring-white hover:ring-blue-400 hover:z-50 transition-all">
                <AvatarImage src={member.profilePicture} alt={member.name} />
                <AvatarFallback className="text-[10px] bg-gray-100">{getInitials(member.name)}</AvatarFallback>
              </Avatar>
              <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity pointer-events-none">
                {member.name}
              </span>
            </div>
          ))}
        </div>
        {/* Teams icon toggle - always visible */}
        <div className="group relative">
          <button
            onClick={toggleDrawer}
            className="flex items-center justify-center p-2 hover:opacity-70 transition-opacity"
          >
            <img src="/teams_logo.png" alt="Teams" className="h-5 w-5 object-contain" />
          </button>
          <span className="absolute top-10 right-0 w-48 px-2 py-1.5 bg-gray-900 text-white text-xs rounded text-center opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity pointer-events-none">
            Quick access to message your team.
          </span>
        </div>
      </div>
    </div>
  )
}

export default FloatingTeamBar
